import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  IconButton,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemButton
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  addDoc, 
  getDocs, 
  doc, 
  Timestamp, 
  getDoc,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate, useLocation } from 'react-router-dom';

const Messages = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const unsubscribeRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Récupérer le paramètre contact de l'URL
  const searchParams = new URLSearchParams(location.search);
  const contactIdFromUrl = searchParams.get('contact');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Nettoyer l'écouteur lors du démontage du composant
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  // Charger les contacts
  useEffect(() => {
    if (!userData?.establishmentId || !currentUser?.uid) return;

    const fetchContacts = async () => {
      try {
        setLoading(true);
        
        // Récupérer les utilisateurs admin et personnel
        const usersQuery = query(
          collection(db, 'users'),
          where('establishmentId', '==', userData.establishmentId)
        );
        
        const usersSnapshot = await getDocs(usersQuery);
        const usersList = usersSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            firstName: doc.data().firstName || '',
            lastName: doc.data().lastName || '',
            role: doc.data().role || ''
          }))
          .filter(user => {
            const isNotCurrentUser = user.uid !== currentUser.uid;
            const isAdmin = user.role === 'admin';
            const isPersonnel = user.role === 'personnel';
            return isNotCurrentUser && (isAdmin || isPersonnel);
          });

        setContacts(usersList);
        
        // Si un contactId est spécifié dans l'URL, sélectionner ce contact
        if (contactIdFromUrl) {
          
          // Essayer de trouver le contact par id ou par uid
          let contactFromUrl = usersList.find(user => user.id === contactIdFromUrl);
          
          // Si pas trouvé par id, essayer par uid
          if (!contactFromUrl) {
            contactFromUrl = usersList.find(user => user.uid === contactIdFromUrl);
          }
          
          
          if (contactFromUrl) {
            setSelectedContact(contactFromUrl);
          } else {
            // Essayer de récupérer l'utilisateur directement depuis Firestore
            const fetchUserById = async () => {
              try {
                const userDoc = await getDoc(doc(db, 'users', contactIdFromUrl));
                if (userDoc.exists()) {
                  const userData = { id: userDoc.id, ...userDoc.data() };
                  setSelectedContact(userData);
                  // Ajouter l'utilisateur à la liste des contacts
                  setContacts(prev => [...prev, userData]);
                } else {
                }
              } catch (err) {
                console.error('Erreur lors de la récupération de l\'utilisateur:', err);
              }
            };
            fetchUserById();
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des contacts:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchContacts();
  }, [userData, currentUser, contactIdFromUrl]);

  // Filtrer et trier les contacts
  const filteredContacts = contacts
    .filter(contact => {
      // Filtre de recherche
      const searchMatch = `${contact.firstName} ${contact.lastName}`.toLowerCase().includes(searchTerm.toLowerCase());
      // Filtre de rôle
      const roleMatch = roleFilter === 'all' || contact.role === roleFilter;
      return searchMatch && roleMatch;
    })
    .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));

  // Mettre à jour l'URL lorsqu'un contact est sélectionné
  useEffect(() => {
    if (selectedContact) {
      // Mettre à jour l'URL sans recharger la page
      const newUrl = `${location.pathname}?contact=${selectedContact.id}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [selectedContact, location.pathname]);
  
  // Écouter les messages en temps réel lorsqu'un contact est sélectionné
  useEffect(() => {
    if (!selectedContact || !userData?.establishmentId) return;

    // Désabonner de l'ancien écouteur si il existe
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    
    const messagesRef = collection(db, 'messages');
    // Créer deux queries : une pour les messages envoyés et une pour les messages reçus
    const sentMessagesQuery = query(
      messagesRef,
      where('establishmentId', '==', userData.establishmentId),
      where('senderId', '==', currentUser.uid),
      where('recipientId', '==', selectedContact.uid || selectedContact.id),
      orderBy('createdAt', 'asc')
    );

    const receivedMessagesQuery = query(
      messagesRef,
      where('establishmentId', '==', userData.establishmentId),
      where('senderId', '==', selectedContact.uid || selectedContact.id),
      where('recipientId', '==', currentUser.uid),
      orderBy('createdAt', 'asc')
    );

    // Combiner les résultats des deux queries
    const unsubscribeSent = onSnapshot(sentMessagesQuery, (sentSnapshot) => {
      const unsubscribeReceived = onSnapshot(receivedMessagesQuery, (receivedSnapshot) => {
        const allMessages = [
          ...sentSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })),
          ...receivedSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
        ].sort((a, b) => a.createdAt.seconds - b.createdAt.seconds);

        setMessages(allMessages);
        scrollToBottom();
      }, (error) => {
        console.error('Erreur dans l\'écouteur des messages reçus:', error);
        setError('Erreur lors de la réception des messages: ' + error.message);
      });

      // Mettre à jour la référence pour le nettoyage
      const oldUnsubscribe = unsubscribeRef.current;
      unsubscribeRef.current = () => {
        unsubscribeSent();
        unsubscribeReceived();
      };
      if (oldUnsubscribe) oldUnsubscribe();
    }, (error) => {
      console.error('Erreur dans l\'écouteur des messages envoyés:', error);
      setError('Erreur lors de la réception des messages: ' + error.message);
    });

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [selectedContact, userData, currentUser]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedContact) return;

    try {
      
      const messageData = {
        content: newMessage.trim(),
        senderId: currentUser.uid,
        senderName: `${userData.firstName} ${userData.lastName}`,
        senderRole: userData.role === 'chefService' ? 'chefService' : userData.role,
        recipientId: selectedContact.uid || selectedContact.id,
        recipientName: `${selectedContact.firstName} ${selectedContact.lastName}`,
        recipientRole: selectedContact.role === 'admin' ? 'admin' : selectedContact.role,
        establishmentId: userData.establishmentId,
        createdAt: Timestamp.now(),
        read: false
      };
      

      await addDoc(collection(db, 'messages'), messageData);
      setNewMessage('');
      scrollToBottom();
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      setError('Erreur lors de l\'envoi du message: ' + error.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/chef-service/dashboard')}
          sx={{ mb: 2 }}
        >
          Retour au tableau de bord
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ height: 'calc(100vh - 200px)', position: 'relative' }}>
        {/* Liste des contacts - colonne fixe à gauche */}
        <Grid item xs={12} md={4} sx={{ height: '100%' }}>
          <Paper sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>
                Contacts
              </Typography>
              
              {/* Filtres */}
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel>Filtrer par rôle</InputLabel>
                  <Select
                    value={roleFilter}
                    label="Filtrer par rôle"
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <MenuItem value="all">Tous les contacts</MenuItem>
                    <MenuItem value="admin">Administration</MenuItem>
                    <MenuItem value="personnel">Personnel</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Rechercher un contact..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                  }}
                />
              </Box>
            </Box>

            {/* Liste des contacts */}
            <List sx={{ flex: 1, overflow: 'auto' }}>
              {filteredContacts.length === 0 ? (
                <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  Aucun contact trouvé
                </Typography>
              ) : (
                filteredContacts.map((contact) => (
                  <ListItem
                    key={contact.id}
                    disablePadding
                    sx={{ px: 2, py: 0.5 }}
                  >
                    <ListItemButton
                      onClick={() => setSelectedContact(contact)}
                      selected={selectedContact?.id === contact.id}
                      sx={{
                        borderRadius: 1,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          }
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: contact.role === 'admin' ? 'error.main' : 'primary.main'
                          }}
                        >
                          {contact.firstName?.[0]?.toUpperCase() || <PersonIcon />}
                        </Avatar>
                      </ListItemAvatar>
                      
                      <ListItemText
                        primary={`${contact.firstName} ${contact.lastName}`}
                        secondary={
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                            >
                              {contact.role === 'admin' ? 'Administration' : 'Personnel'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>

        {/* Zone de chat */}
        <Grid item xs={12} md={8} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {selectedContact ? (
              <>
                {/* En-tête du chat - fixe en haut */}
                <Box sx={{ 
                  p: 2, 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.paper',
                  zIndex: 1
                }}>
                  <Typography variant="h6">
                    {selectedContact ? `${selectedContact.firstName} ${selectedContact.lastName}` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedContact.role === 'admin' ? 'Administration' : 'Personnel'}
                  </Typography>
                </Box>

                {/* Messages - zone défilante indépendante */}
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  p: 2, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 2,
                  height: 'calc(100% - 140px)', /* Hauteur calculée pour laisser de la place pour l'en-tête et la zone de saisie */
                  mb: '70px' /* Marge en bas pour éviter que les derniers messages soient cachés par la zone de saisie */
                }}>
                  {messages.map((message) => {
                    const isCurrentUser = message.senderId === currentUser.uid;
                    return (
                      <Box
                        key={message.id}
                        sx={{
                          display: 'flex',
                          justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                          mb: 1
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '70%',
                            bgcolor: isCurrentUser ? 'primary.main' : 'grey.100',
                            color: isCurrentUser ? 'white' : 'text.primary',
                            borderRadius: 2,
                            p: 2,
                            position: 'relative'
                          }}
                        >
                          <Typography variant="body1">
                            {message.content}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                            {format(message.createdAt.toDate(), 'HH:mm', { locale: fr })}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Zone de saisie - fixe en bas */}
                <Box
                  component="form"
                  onSubmit={handleSendMessage}
                  sx={{
                    p: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    gap: 1,
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Écrivez votre message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    multiline
                    maxRows={4}
                  />
                  <IconButton
                    color="primary"
                    type="submit"
                    disabled={!newMessage.trim()}
                  >
                    <SendIcon />
                  </IconButton>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}
              >
                <Typography variant="h6">
                  Sélectionnez un contact pour démarrer une conversation
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Messages;